import React, { useState, useEffect, useCallback } from 'react';
import { FaFileAlt, FaUpload, FaTrash, FaSpinner, FaEye, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { API_URL } from '../config';

// ============================================
// DOCUMENTS FOR ONE LOAD - upload, name, view, download
// ============================================
// Anything that belongs to a single load rather than to the job as a whole: a border permit, a
// "proceed to border" letter, a customs release, an escort letter. They live in the generic
// `documents` table under entity_type 'load', so the office, the subbie and the client all look at
// the same pile of paper.
//
// Files are stored by the backend under /uploads/documents/... and served from the API host, so the
// link is built with API_URL. A bare "/uploads/..." href would hit the frontend's own host and bounce
// the user to the dashboard - the same trap as the invoice PO link (bug B3 in PROJECT-NOTES.md).
//
// WHO CAN DO WHAT: everyone may upload, name and download. Delete is offered to admins only.
// ============================================

const fileUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_URL}${String(path).startsWith('/') ? '' : '/'}${path}`;
};

const fmtSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtWhen = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

function LoadDocumentsPanel({ loadId, canDelete = false, heading = 'Documents' }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);

  const loadDocs = useCallback(async () => {
    if (!loadId) { setDocs([]); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/documents/load/${loadId}`);
      const data = await res.json();
      setDocs(Array.isArray(data.documents) ? data.documents : []);
    } catch (err) {
      console.error('Error loading load documents:', err);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [loadId]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Give the document a name'); return; }
    if (!file) { toast.error('Choose a file to upload'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('entity_type', 'load');
      fd.append('entity_id', loadId);
      fd.append('document_type', 'load_document');
      fd.append('description', name.trim());   // the name the user typed shows in the list
      const res = await fetch(`${API_URL}/documents/upload`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Upload failed');
      toast.success('Document uploaded');
      setName('');
      setFile(null);
      setShowForm(false);
      loadDocs();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc) => {
    const label = doc.description || doc.file_name || 'this document';
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/documents/${doc.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Document deleted');
      loadDocs();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  return (
    <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold flex items-center gap-2" style={{ color: '#0A1929' }}>
          <FaFileAlt size={12} style={{ color: '#00A86B' }} /> {heading}
          {docs.length > 0 && <span className="text-xs font-normal text-gray-500">({docs.length})</span>}
        </h3>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-white"
            style={{ backgroundColor: '#00A86B' }}
          >
            <FaPlus size={10} /> Upload document
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleUpload} className="mb-4 rounded-lg border p-3" style={{ borderColor: '#E8ECEF', backgroundColor: '#FAFAFA' }}>
          <div className="mb-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">What is it? (the name shown in the list)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Proceed to border letter"
              className="w-full px-3 py-2 border rounded-lg text-sm"
              style={{ borderColor: '#E8ECEF' }}
            />
          </div>
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">File (PDF, JPG, PNG, DOC, DOCX - max 10MB)</label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={(e) => setFile(e.target.files[0] || null)}
              className="w-full text-sm"
            />
            {file && <p className="text-xs text-green-600 mt-1">{file.name} ({fmtSize(file.size)})</p>}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowForm(false); setName(''); setFile(null); }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border hover:bg-gray-50"
              style={{ borderColor: '#E8ECEF' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: '#00A86B' }}
            >
              {uploading ? <FaSpinner className="animate-spin" size={11} /> : <FaUpload size={11} />}
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-gray-400 flex items-center gap-2"><FaSpinner className="animate-spin" /> Loading documents...</p>
      ) : docs.length === 0 ? (
        <p className="text-sm text-gray-400">No documents on this load yet.</p>
      ) : (
        <ul className="space-y-2">
          {docs.map((doc) => (
            <li key={doc.id} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2" style={{ borderColor: '#E8ECEF' }}>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: '#0A1929' }}>
                  {doc.description || doc.file_name || 'Document'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {doc.file_name}
                  {doc.file_size ? ` - ${fmtSize(doc.file_size)}` : ''}
                  {doc.created_at ? ` - ${fmtWhen(doc.created_at)}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <a
                  href={fileUrl(doc.file_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium hover:underline"
                  style={{ color: '#00A86B' }}
                  title="View"
                >
                  <FaEye size={11} /> View
                </a>
                <a
                  href={fileUrl(doc.file_url)}
                  download={doc.file_name || undefined}
                  className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:underline"
                  title="Download"
                >
                  Download
                </a>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => handleDelete(doc)}
                    className="text-gray-400 hover:text-red-600"
                    title="Delete"
                  >
                    <FaTrash size={11} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default LoadDocumentsPanel;

